import isEmpty from 'lodash/isEmpty'
import { getWindow, hasWindow } from '@ztechapps/common.utils'
import { useConfirmDialog, useHistory } from '@ztechapps/common.hook'
import { useContext as useOrderContext } from '../components/Order/Context'
import { routesDef } from '../config/routes'
import { storageKeys } from '../config/StorageKeys'

export type UseOrderSignInGuardProps = {
  redirectUrl: string
}

export type UseOrderSignInGuardResult = {
  guard: (onMatch: () => void) => Promise<void>
}

/**
 * Gates a payment action behind "signed in as the order owner". Runs `onMatch()`
 * immediately when the current session matches `document.by._id`; otherwise shows a
 * generic, PII-free confirm dialog and only on confirm clears the stale local session
 * and redirects to sign-in with `redirectUrl`, so the user lands back here afterward.
 */
export const useOrderSignInGuard = ({ redirectUrl }: UseOrderSignInGuardProps): UseOrderSignInGuardResult => {
  const { document } = useOrderContext()
  const history = useHistory()
  const current = hasWindow() ? getWindow()?.localStorage?.getItem(storageKeys.userId) : undefined

  const { confirm } = useConfirmDialog({
    title: 'Sign in to continue',
    text: "You don't have access to pay this invoice. Please sign in to continue — you'll be redirected to the sign-in page.",
  })

  const guard = async (onMatch: () => void) => {
    const target = document?.by?._id

    if (!isEmpty(target) && current === target) {
      onMatch()
      return
    }

    // Mismatch (no / wrong / expired session): prompt before clearing localStorage and redirecting.
    // localStorage wipe MUST happen only on confirm — cancelling must not nuke someone else's session.
    await confirm(async () => {
      if (hasWindow()) {
        const storage = getWindow()?.localStorage
        if (storage?.getItem(storageKeys.accessToken) || storage?.getItem(storageKeys.userId)) {
          storage.removeItem(storageKeys.accessToken)
          storage.removeItem(storageKeys.userId)
        }
      }
      history.push(routesDef.LoginPage.altPath({ redirect_url: redirectUrl }))
    })
  }

  return { guard }
}
